// Store
import { store } from '../../redux';
import { setRequestState } from '../../redux/appreadouts/actions';
import {
    IValidateError,
    IError,
    ITranslateKeyError,
    IBatchResponse,
    IBatchError,
} from '../../interfaces/general.interfaces';
// Interfaces
import { IErrorCodeOptions, IMiddlewareOptions } from './interfaces';
// Helpers
import { isResponseStatusHandled, redirectToSystemError } from './helpers';
import { httpErrorCodes, getTranslateErrorKey } from '../../helpers/errors';
// Config
import { staticConfig } from '../../config/static';
import { setIsAuthorized } from '../../redux/user/actions';
import { logoutUser } from '../../redux/user/actions';
import { clearAuthStorage } from '../../helpers';

const settings = {
    apiBaseUrl: staticConfig.apiBaseUrl,
};

export const typedRequest = <T>(
    url: string,
    options: RequestInit = {},
    middlewareOptions?: IMiddlewareOptions
): Promise<T> =>
    new Promise(async (resolve, reject) => {
        const noCredentials = middlewareOptions?.noCredentials;

        const isUrlExclusive = middlewareOptions?.options?.isUrlExclusive;

        const errorCodeOptions = middlewareOptions?.errorCodeOptions;

        const fetchUrl = isUrlExclusive ? url : settings.apiBaseUrl + url;

        if (!noCredentials) {
            options.credentials = 'include';
        }

        try {
            store.dispatch(setRequestState(true));
            await executeFetchRequest(fetchUrl, options, errorCodeOptions, resolve, reject);
        } catch (error) {
            reject(error);
        } finally {
            store.dispatch(setRequestState(false));
        }
    });

const executeFetchRequest = async <T>(
    fetchUrl: string,
    options: RequestInit,
    errorCodeOptions: IErrorCodeOptions,
    resolve: (value: T | PromiseLike<T>) => void,
    reject: (reason?: any) => void
): Promise<void> => {
    const response = await fetch(fetchUrl, options);

    const responseStatusHandled = isResponseStatusHandled(response, errorCodeOptions);

    if (!response.ok && ![400, 401, 403, 404].includes(response.status) && !responseStatusHandled) {
        redirectToSystemError(response, null);
        reject();
    }

    const rawResponse = await response.text();

    const responseBody = parseRawResponse(rawResponse);

    if (!isBatchResponse(responseBody) || !response.ok) {
        resolveBody(resolve, reject, responseBody, responseStatusHandled, response, errorCodeOptions);
        return;
    }

    const batchResponseFallbackResponse = tryResolveAsBatchResponse(responseBody, resolve);

    if (batchResponseFallbackResponse) {
        resolveBody(resolve, reject, batchResponseFallbackResponse, responseStatusHandled, response, errorCodeOptions);
    }
};

const parseRawResponse = (rawResponse: string): any => {
    try {
        return JSON.parse(rawResponse);
    } catch {
        return rawResponse;
    }
};

const tryResolveAsBatchResponse = <T>(responseBody: any, resolve: (value: T | PromiseLike<T>) => void): IBatchError => {
    const batchResponse = responseBody as IBatchResponse<T>;

    if (
        (batchResponse.requestItemCount > 1 && batchResponse.errorList.length < batchResponse.requestItemCount) ||
        batchResponse.requestItemCount === 1
    ) {
        // partial ok and error response or only item in request, let caller handle
        resolve(responseBody);
        return undefined;
    }

    if (
        batchResponse.errorList.every((error) => error.errorCode === batchResponse.errorList[0].errorCode) &&
        batchResponse.errorList[0].errorCode === 400 &&
        batchResponse.errorList[0].validationErrors &&
        batchResponse.errorList.every(
            (error) =>
                error.validationErrors[0].validationErrorCode ===
                batchResponse.errorList[0].validationErrors[0].validationErrorCode
        )
    ) {
        return batchResponse.errorList[0];
    }

    resolve(responseBody);
    return undefined;
};

const isBatchResponse = (responseBody: any): boolean =>
    responseBody.itemList && responseBody.requestItemCount && responseBody.errorList;

const resolveBody = <T>(
    resolve: (value: T | PromiseLike<T>) => void,
    reject: (reason?: any) => void,
    resBody: any,
    responseStatusHandled: boolean,
    response: Response,
    errorCodeOptions?: IErrorCodeOptions
): void => {
    const errorList: Array<IError | ITranslateKeyError> = [];

    if ((resBody.validationErrors && resBody.validationErrors.length > 0) || resBody.errorCode) {
        if (resBody.validationErrors) {
            resBody.validationErrors.forEach((error: IValidateError) => {
                if (!error.validationErrorCode) {
                    return;
                }

                const batchError = resBody as IBatchError;

                if (responseStatusHandled) {
                    errorList.push({
                        contextId: batchError.contextId || null,
                        errorCode: error.validationErrorCode,
                        propertyName: error.propertyName,
                    });

                    return;
                }

                if (error.validationErrorCode === 99999) {
                    store.dispatch(setIsAuthorized(false));
                    store.dispatch(logoutUser());
                    return;
                }

                if (!httpErrorCodes[error.validationErrorCode]) {
                    redirectToSystemError(response, error.validationErrorCode);
                    reject();
                }

                errorList.push(
                    getTranslateErrorKey(
                        batchError.contextId || null,
                        error.validationErrorCode,
                        error.propertyName,
                        errorCodeOptions && errorCodeOptions.errorCodeComponentContext
                    )
                );
            });
        } else if (resBody.errorCode) {
            const batchError = resBody as IBatchError;

            if (responseStatusHandled) {
                errorList.push({
                    contextId: batchError.contextId || null,
                    errorCode: resBody.errorCode,
                    propertyName: resBody.propertyName,
                });
            } else {
                if (!httpErrorCodes[resBody.errorCode]) {
                    redirectToSystemError(response, resBody.errorCode);
                    reject();
                }

                errorList.push(
                    getTranslateErrorKey(
                        batchError.contextId || null,
                        resBody.errorCode,
                        resBody.propertyName,
                        errorCodeOptions && errorCodeOptions.errorCodeComponentContext
                    )
                );
            }
        }

        reject(errorList);
    } else if (resBody.error_description) {
        reject(resBody);
        return;
    } else {
        if (response.ok) {
            resolve(resBody);
            return;
        }

        if (responseStatusHandled) {
            reject({
                status: response.status,
                body: resBody,
            });
            return;
        }

        if (response.status === 404 && httpErrorCodes[response.status]) {
            const batchError = resBody as IBatchError;

            errorList.push(
                getTranslateErrorKey(
                    batchError.contextId || null,
                    response.status,
                    null,
                    errorCodeOptions && errorCodeOptions.errorCodeComponentContext
                )
            );

            reject(errorList);
            return;
        }

        if (response.status == 401) {
            clearAuthStorage();
            window.location.href = staticConfig.appBaseUrl.slice(0, -1) + '/?logout=done';
            return;
        }
        if (response.status == 403) {
            const batchError = resBody as IBatchError;
            errorList.push(
                getTranslateErrorKey(
                    batchError.contextId || null,
                    response.status,
                    null,
                    errorCodeOptions.errorCodeComponentContext
                )
            );
            reject(errorList);
            return;
        }

        redirectToSystemError(response, null);
    }
};
